import type { ReportCitation } from "@/types";
import { formulaArgumentText, isOutputFormat } from "./ask-parser";

// Pure helpers of the PERFORMANCE formula, kept apart from functions.ts so they load without
// Office, the API client or the shared state.

export type PerformanceQueryStatus =
  | "answered"
  | "no_reports"
  | "report_not_found"
  | "ambiguous_fund"
  | "ambiguous"
  | "period_not_found"
  | "not_found";

export type PerformanceQueryResponse = {
  id: string;
  question: string;
  answer: string | null;
  sourcePath: "fund_reporting" | "firm_aum";
  reportCitations: ReportCitation[];
  performance: {
    status: PerformanceQueryStatus;
    scope: { kind: "firm" | "product"; productId?: string };
    report: { importId: string; label: string };
    period: string | null;
    fundNames: string[];
    availablePeriods: string[];
  };
};

// Excel stores dates as day serials from 1899-12-30; a date cell passed as the quarter arrives as
// a number and is sent as an ISO date the backend reads as a period ("2025-12-31" -> Q4 2025).
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const FIRST_DATE_SERIAL = 20000; // 1954-10-03
const LAST_DATE_SERIAL = 80000; // 2119-01-11

/** The formula's quarter argument as text: a value, a one-cell range, or a date cell. */
export function quarterArgumentText(value: unknown): string | undefined {
  const raw = Array.isArray(value) ? (value.flat(2)[0] as unknown) : value;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= FIRST_DATE_SERIAL && raw <= LAST_DATE_SERIAL) {
    return new Date(EXCEL_EPOCH_MS + Math.floor(raw) * MS_PER_DAY).toISOString().slice(0, 10);
  }
  return formulaArgumentText(value) ?? undefined;
}

/**
 * PERFORMANCE(input, [quarter], [parameters]...): the quarter sits between the question and the
 * repeating parameters, and an omitted quarter arrives empty. An output format written in the
 * quarter position, as in =AIIR.PERFORMANCE("Fund NAV", "number"), is the format, as it would be
 * anywhere in ASK. Returns the arguments for ASK's parser and the quarter to send.
 */
export function splitPerformanceArguments(rawUserArgs: unknown[]): {
  parserArgs: unknown[];
  quarter: string | undefined;
} {
  const [questionArg, quarterArg, ...parameterArgs] = rawUserArgs;
  const quarter = quarterArgumentText(quarterArg);
  if (quarter !== undefined && isOutputFormat(quarter)) {
    return { parserArgs: [questionArg, quarterArg, ...parameterArgs], quarter: undefined };
  }
  return { parserArgs: [questionArg, ...parameterArgs], quarter };
}

/** The cell text for a query the selected performance or AUM report could not answer. */
export function performanceNotFoundMessage(performance: PerformanceQueryResponse["performance"]): string {
  const source = performance.scope.kind === "firm" ? "AUM report" : "fund report";
  if (performance.status === "no_reports") {
    return `No available ${source} found. Select a reporting period in the Aiir add-in.`;
  }

  if (performance.status === "report_not_found") {
    return `The selected ${source} is no longer available. Select another reporting period in the Aiir add-in.`;
  }

  if (performance.status === "ambiguous_fund") {
    return "Multiple funds match this question. Select a fund and reporting period in the Aiir add-in.";
  }

  if (performance.status === "ambiguous") {
    return "Multiple values match this question. Add context cells to identify the required value.";
  }

  if (performance.status === "period_not_found") {
    const period = performance.period ? ` for ${performance.period}` : "";
    const available =
      performance.availablePeriods.length > 0 ? ` Available: ${performance.availablePeriods.join(", ")}.` : "";
    return `The selected ${source} has no data${period}.${available}`;
  }

  return `Aiir could not find this in the selected ${source}.`;
}
